import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Navigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import {
  canEditSettingsSection,
  canViewSettingsSection,
  settingsPathToSectionKey,
  type SettingsSectionKey,
} from '@/lib/settings-access.js';
import { cn } from '@/lib/utils.js';

const COLLAPSE_STORAGE_KEY = 'fixflow-settings-nav-collapsed';

type NavItem = {
  to: string;
  label: string;
  section: SettingsSectionKey;
};

type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

const SETTINGS_NAV: NavGroup[] = [
  {
    id: 'store',
    label: 'Store',
    items: [
      { to: '/settings/store', label: '📍 Store Info', section: 'store' },
      { to: '/settings/printing', label: '🖨 Printing & Display', section: 'printing' },
      { to: '/settings/payments', label: '💳 Payments & Tax', section: 'payments' },
      { to: '/settings/integrations', label: '🔗 Integrations', section: 'integrations' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { to: '/settings/tickets', label: '🎫 Ticket Settings', section: 'tickets' },
      { to: '/settings/ticket-statuses', label: '🏷 Ticket Statuses', section: 'tickets' },
      { to: '/settings/notifications', label: '💬 Notifications', section: 'notifications' },
      { to: '/settings/appointments', label: '📅 Appointments', section: 'appointments' },
      { to: '/settings/waivers', label: '📝 Waivers', section: 'waivers' },
      { to: '/settings/loyalty', label: '🎁 Loyalty & Referrals', section: 'loyalty' },
      { to: '/settings/cash-sessions', label: '💵 Cash Sessions', section: 'cash_sessions' },
    ],
  },
  {
    id: 'staff',
    label: 'Staff & Security',
    items: [
      { to: '/settings/staff', label: '👥 Staff', section: 'staff' },
      { to: '/settings/permissions', label: '🔒 Permissions', section: 'permissions' },
      { to: '/settings/approvals', label: '✅ Remote Approvals', section: 'approvals' },
      { to: '/settings/pins', label: '🔢 PIN Settings', section: 'pins' },
      { to: '/settings/audit-log', label: '📋 Audit Log', section: 'audit_log' },
    ],
  },
  {
    id: 'customization',
    label: 'Customization',
    items: [
      { to: '/settings/repair-services', label: '🔧 Repair Services', section: 'repair_services' },
      { to: '/settings/inventory-categories', label: '📂 Inventory Categories', section: 'inventory_categories' },
      { to: '/settings/suppliers', label: '🚚 Suppliers', section: 'suppliers' },
      { to: '/settings/pricing-matrix', label: '💰 Pricing Matrix', section: 'pricing_matrix' },
      { to: '/settings/warranty-rules', label: '🛡 Warranty Rules', section: 'warranty_rules' },
      { to: '/settings/forms', label: '📋 Custom Forms', section: 'forms' },
      { to: '/settings/templates', label: '🎫 Ticket Templates', section: 'templates' },
      { to: '/settings/snippets', label: '✂ Note Snippets', section: 'snippets' },
      { to: '/settings/fee-presets', label: '💰 Fee Presets', section: 'fee_presets' },
      { to: '/settings/discounts', label: '🏷 Discount Presets', section: 'discounts' },
      { to: '/settings/conditions', label: '📱 Device Conditions', section: 'conditions' },
      { to: '/settings/devices', label: '📱 Device Catalog', section: 'devices' },
      { to: '/settings/pos', label: '🛒 POS Settings', section: 'pos' },
      { to: '/settings/care-guides', label: '📄 Care Guides', section: 'care_guides' },
      { to: '/settings/upsell', label: '💡 Upsell Suggestions', section: 'upsell' },
      { to: '/settings/duplicate-customers', label: '👥 Duplicate Customers', section: 'duplicate_customers' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      { to: '/settings/profile', label: '👤 My Profile', section: 'profile' },
      { to: '/settings/locations', label: '🏪 Locations', section: 'locations' },
      { to: '/settings/migration', label: '📥 Data Migration', section: 'migration' },
      { to: '/settings/data-cleanup/balances', label: '🧹 Balances', section: 'data_cleanup' },
      { to: '/settings/data-cleanup/parts-costs', label: '🔧 Parts Costs', section: 'data_cleanup' },
      { to: '/settings/data-cleanup/inventory-duplicates', label: '📦 Inventory Duplicates', section: 'data_cleanup' },
      { to: '/settings/data-cleanup/device-compatibility', label: '📱 Device Compatibility', section: 'data_cleanup' },
      { to: '/settings/billing', label: '💳 Billing', section: 'billing' },
    ],
  },
];

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function breadcrumbLabel(pathname: string): string {
  for (const group of SETTINGS_NAV) {
    const item = group.items.find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
    if (item) return item.label.replace(/^[^\s]+\s/, '');
  }
  if (pathname.includes('/repair-services/')) return 'Repair Service';
  if (pathname.includes('/forms/')) return 'Custom Forms';
  return 'Settings';
}

function firstVisibleSettingsPath(
  flags: ReturnType<typeof usePermissions>['flags'],
  role: string,
): string {
  for (const group of SETTINGS_NAV) {
    for (const item of group.items) {
      if (canViewSettingsSection(flags, role, item.section)) return item.to;
    }
  }
  return '/settings/profile';
}

export function SettingsLayout({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'read_only';
  const { flags, isLoading } = usePermissions();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => loadCollapsed());

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  const visibleGroups = useMemo(
    () =>
      SETTINGS_NAV.map((group) => ({
        ...group,
        items: group.items.filter((item) => canViewSettingsSection(flags, role, item.section)),
      })).filter((g) => g.items.length > 0),
    [flags, role],
  );

  const activeGroupId = useMemo(() => {
    for (const group of visibleGroups) {
      if (group.items.some((i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`))) {
        return group.id;
      }
    }
    return null;
  }, [location.pathname, visibleGroups]);

  const sectionKey = settingsPathToSectionKey(location.pathname);
  const canViewCurrent =
    !sectionKey || isLoading || canViewSettingsSection(flags, role, sectionKey);
  const canEditCurrent =
    !sectionKey || isLoading || canEditSettingsSection(flags, role, sectionKey);

  function toggleGroup(id: string) {
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  }

  if (!isLoading && sectionKey && !canViewCurrent) {
    return <Navigate to={firstVisibleSettingsPath(flags, role)} replace />;
  }

  const sidebar = (
    <nav className="space-y-1">
      {visibleGroups.map((group) => {
        const isExpanded = collapsed[group.id] !== true || activeGroupId === group.id;
        return (
          <div key={group.id} className="mb-2">
            <button
              type="button"
              className="flex w-full items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-400 hover:text-slate-600"
              onClick={() => toggleGroup(group.id)}
            >
              {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {group.label}
            </button>
            {isExpanded ? (
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to !== '/settings/forms' && item.to !== '/settings/repair-services'}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
                        isActive ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 lg:hidden">
        <p className="text-sm text-slate-500">
          Settings → <span className="font-semibold text-slate-800">{breadcrumbLabel(location.pathname)}</span>
        </p>
        <button
          type="button"
          className="rounded-xl border border-slate-300 p-2 text-slate-700"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle settings menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[256px_1fr]">
        <aside
          className={cn(
            'h-fit rounded-2xl border border-slate-200 bg-white p-2 shadow-sm lg:sticky lg:top-4',
            mobileOpen ? 'block' : 'hidden lg:block',
          )}
        >
          <p className="hidden px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-400 lg:block">
            Settings
          </p>
          {sidebar}
        </aside>

        <div
          className={cn(
            'min-w-0',
            location.pathname.includes('/pricing-matrix') ||
              location.pathname.includes('/warranty-rules')
              ? 'max-w-none'
              : 'max-w-3xl',
          )}
        >
          <p className="mb-4 hidden text-sm text-slate-500 lg:block">
            Settings → <span className="font-semibold text-slate-800">{breadcrumbLabel(location.pathname)}</span>
          </p>
          {!isLoading && sectionKey && canViewCurrent && !canEditCurrent ? (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              View only — your role can see these settings but cannot save changes.
            </div>
          ) : null}
          <div
            {...(!isLoading && sectionKey && canViewCurrent && !canEditCurrent
              ? ({ inert: true } as React.HTMLAttributes<HTMLDivElement>)
              : {})}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
